import { useRecoilState } from 'recoil';

import { useNavigate } from 'react-router-dom';
import { authState, User } from 'states';
import { RouteConstants } from '../constants';

export { useFetchWrapper };

interface ApiResponseBody {
  statusInfo?: { statusCode?: number; errorMessage?: string };
  message?: string;
  error?: string;
  data?: unknown;
  responseData?: unknown;
}

const getErrorMessage = (data: ApiResponseBody | null, statusText: string) =>
  data?.statusInfo?.errorMessage || data?.message || data?.error || statusText;

// The backend reports some write failures as HTTP 200 with an error in
// statusInfo (e.g. 320 "Process failed"). Surface it so callers don't show a
// success toast. GETs are left alone: 320 can also mean "no records".
const getStatusInfoError = (data: ApiResponseBody | null, method: string) =>
  method !== 'GET' ? data?.statusInfo?.errorMessage : undefined;

const unwrapPayload = (data: ApiResponseBody | null) => data?.data ?? data?.responseData ?? data;

function useFetchWrapper() {
  const [auth, setAuth] = useRecoilState(authState);
  const navigate = useNavigate();

  return {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    delete: request('DELETE'),
  };

  function request(method: string) {
    // T is the payload shape the calling service expects; the backend gives no
    // runtime guarantee of it, so it is an assertion, not validation.
    return <T = unknown>(url: URL, body?: any): Promise<T> => {
      const requestOptions: any = {
        method,
        headers: authHeader(url) as any,
      };
      if (body) {
        requestOptions.headers['Content-Type'] = 'application/json';
        requestOptions.body = JSON.stringify(body);
      }
      return fetch(url.toString(), requestOptions).then((response) => handleResponse<T>(response, method));
    };
  }

  function authHeader(url: URL) {
    const token = auth?.authToken;
    const isLoggedIn = !!token;
    const isApiUrl = url.toString().startsWith(process.env.REACT_APP_BASE_API_URL!);
    if (isLoggedIn && isApiUrl) {
      return { Authorization: `Bearer ${token}` };
    } else {
      return {};
    }
  }

  function handleResponse<T>(response: Response, method: string): Promise<T> {
    return response.text().then((text) => {
      const data: ApiResponseBody | null = text ? JSON.parse(text) : null;

      if (!response.ok) {
        // Only 401 means the session itself is invalid; a 403 can just be a
        // permission the user's role lacks, which shouldn't log them out.
        if (response.status === 401 && auth?.authToken) {
          logout();
        }
        return Promise.reject(getErrorMessage(data, response.statusText));
      }

      const statusInfoError = getStatusInfoError(data, method);
      return statusInfoError ? Promise.reject(statusInfoError) : (unwrapPayload(data) as T);
    });
  }

  function logout() {
    // remove user from local storage, set auth state to null and redirect to login page
    localStorage.removeItem('user');
    setAuth({} as User);
    navigate(RouteConstants.login);
  }
}